import { PlugConfig } from "@/types/plugConfig";
import plugConfigApi from "@/types/plugConfig/plugConfigApi";
import { mergePlugConfigs } from "@/Utils/plugConfig";
import { callApi } from "@/Utils/request/query";
import { loadTranslationResources } from "@/Utils/startup/translationResources";
import careConfig from "@careConfig";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";
import { z } from "zod";
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths -- Reuse canonical locale assets outside src.
import en from "../public/locale/en.json";
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths -- Reuse canonical locale assets outside src.
import nl from "../public/locale/nl.json";

// CARE Suriname ships Dutch and English only; the upstream Indian-language
// files were removed on 16 September 2026 (they had at most a third of the
// keys translated and are not used in Suriname).
export const LANGUAGES = {
  en: "English",
  nl: "Nederlands",
} as const;

const DEFAULT_NAMESPACE = "care_fe";

let pluginConfigs: PlugConfig[] = [];

const namespaceToUrl = (namespace: string) => {
  if (namespace === DEFAULT_NAMESPACE) {
    return "/";
  }

  const pluginConfig = pluginConfigs.find(
    (config) => config.meta?.name === namespace || config.slug === namespace,
  );

  if (typeof pluginConfig?.meta?.localPath === "string") {
    return pluginConfig.meta.localPath;
  }

  if (
    pluginConfig?.meta?.url &&
    z.string().url().safeParse(pluginConfig.meta.url).success
  ) {
    const url = new URL(pluginConfig.meta.url);
    return url.origin.toString();
  }

  return undefined;
};

// Local Dutch/English are part of the application, independent of plugin discovery.
export async function initI18n() {
  pluginConfigs = mergePlugConfigs();
  await i18n
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(
      resourcesToBackend((language, namespace, callback) => {
        const baseUrl = namespaceToUrl(namespace)?.replace(/\/$/, "");
        if (!baseUrl && namespace !== DEFAULT_NAMESPACE) {
          callback(null, {});
          return;
        }
        loadTranslationResources(`${baseUrl}/locale/${language}.json`)
          .then((resources) => callback(null, resources))
          .catch((error: Error) => callback(error, null));
      }),
    )
    .init({
      resources: {
        en: { [DEFAULT_NAMESPACE]: en },
        nl: { [DEFAULT_NAMESPACE]: nl },
      },
      partialBundledLanguages: true,
      fallbackLng: "en",
      ns: [DEFAULT_NAMESPACE],
      load: "currentOnly",
      supportedLngs: Object.keys(LANGUAGES),
      interpolation: { escapeValue: false, skipOnVariables: false },
      defaultNS: DEFAULT_NAMESPACE,
    });
  // Optional resources enrich an already usable application; they never gate it.
  void loadOptionalTranslations();
  if (careConfig.i18nUrl) {
    void loadRemoteOverrides(i18n.resolvedLanguage ?? "en");
    i18n.on("languageChanged", (language) => {
      void loadRemoteOverrides(language);
    });
  }
}

async function loadOptionalTranslations() {
  try {
    const response = await callApi(plugConfigApi.list, {
      silent: true,
      signal: AbortSignal.timeout(5000),
    });
    pluginConfigs = mergePlugConfigs(response.configs || []);
  } catch {
    pluginConfigs = mergePlugConfigs();
  }
  const namespaces = pluginConfigs
    .map((config) => config.meta?.name || config.slug)
    .filter((name): name is string => !!name);
  await i18n.loadNamespaces(namespaces);
}

async function loadRemoteOverrides(language: string) {
  if (careConfig.i18nUrl) {
    try {
      const resources = await loadTranslationResources(
        `${careConfig.i18nUrl}/${language}.json`,
      );
      i18n.addResourceBundle(
        language,
        DEFAULT_NAMESPACE,
        resources,
        true,
        true,
      );
    } catch {
      // Bundled translations remain available after remote failure.
    }
  }
}

export default i18n;
